import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FileValidationError,
  formatFileSizeLabel,
  validateFile,
} from '@/utils/file';

type UseDropzoneParams = {
  accept: string;
  maxSize: number;
  onSelect: (file: File) => void;
};

export default function useDropzone({
  accept,
  maxSize,
  onSelect,
}: UseDropzoneParams) {
  const { t } = useTranslation('translation', { keyPrefix: 'fileUpload' });
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rejection, setRejection] = useState<FileValidationError>();

  const selectFile = (file?: File) => {
    if (!file) return;
    const error = validateFile({ file, accept, maxSize });
    setRejection(error);
    if (!error) onSelect(file);
  };

  const openFilePicker = () => inputRef.current?.click();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0]);
    event.target.value = '';
  };

  const handleDragEnter = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragging(false);
    selectFile(event.dataTransfer.files[0]);
  };

  const getMessage = () => {
    if (isDragging) {
      return { title: t('dropToUpload') };
    }
    if (rejection?.type === 'format') {
      return {
        title: t('unsupportedFormatTitle'),
        description: t('unsupportedFormatDescription', {
          name: rejection.file.name,
          accept: accept
            .split(',')
            .map(type => type.trim())
            .join(', '),
        }),
      };
    }
    if (rejection?.type === 'size') {
      return {
        title: t('fileTooLargeTitle', { max: formatFileSizeLabel(maxSize) }),
        description: t('fileTooLargeDescription', {
          name: rejection.file.name,
          size: formatFileSizeLabel(rejection.file.size),
        }),
      };
    }
    return undefined;
  };

  return {
    inputRef,
    isDragging,
    isRejected: !isDragging && !!rejection,
    message: getMessage(),
    openFilePicker,
    handleChange,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
